import React, { useEffect, useRef, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SyncIcon from "@mui/icons-material/Sync";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import LensIcon from "@mui/icons-material/Lens";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import PanoramaHorizontalIcon from "@mui/icons-material/PanoramaHorizontal";
import ViewCarouselSharpIcon from "@mui/icons-material/ViewCarouselSharp";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SettingsIcon from "@mui/icons-material/Settings";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import * as THREE from "three";
import {
  createPatrolEquirectSphereMesh,
  createPatrolEquirectVideoTexture,
} from "./patrolEquirectRenderer.js";
import CircularProgress from "@mui/material/CircularProgress";
import PanoramaHorizontalSelectIcon from "@mui/icons-material/PanoramaHorizontalSelect";
import ThreeDRotationIcon from "@mui/icons-material/ThreeDRotation";

/**
 * PatrolVideoPlayer Component (reusable)
 *
 * A video player for sequential patrol videos with:
 * - Play/pause, previous/next, flat and 360-degree viewing modes
 * - Mouse rotation for 360-degree videos
 *
 * Optional sync mode (all sync props are optional; omit when using standalone):
 * - syncMode: when true, player accepts controlled props and notifies parent on changes
 * - controlledIsPlaying, controlledCurrentTime, controlledVideoIndex: master playback state
 * - controlledYaw, controlledPitch: master 360 rotation (degrees) so both panels move together
 * - onPlayPauseChange, onTimeUpdate, onVideoIndexChange, onRotationChange: callbacks when user acts
 *
 * @param {Array<string>} videos - Array of video URLs
 * @param {number} initialVideoIndex - Initial video index (default: 0)
 * @param {boolean} autoPlay - Unused; auto-play is by index: index 0 = false, index !== 0 = true (default: false)
 * @param {Function} onSettingsClick - Optional callback when settings clicked
 * @param {Function} getCurrentVideoIndex - Optional callback when video index changes
 * @param {boolean} [syncMode=false] - Enable sync mode; all sync props below are optional
 * @param {boolean} [syncValidForPlayback] - When sync mode is on, video loads only when this is true (both panels have compatible patrols)
 * @param {boolean} [controlledIsPlaying] - Optional master play state
 * @param {number} [controlledCurrentTime] - Optional master seek time: total elapsed seconds (across all videos) for second-wise sync
 * @param {number} [controlledVideoIndex] - Optional master video index (ignored when controlledCurrentTime is set)
 * @param {Function} [onPlayPauseChange] - Optional (isPlaying) when user toggles play/pause
 * @param {Function} [onTimeUpdate] - Optional (totalElapsedSeconds) on timeupdate when sync; total elapsed across all videos
 * @param {Function} [onVideoIndexChange] - Optional (index) when user changes video
 * @param {number} [controlledYaw] - Optional master 360 yaw (degrees)
 * @param {number} [controlledPitch] - Optional master 360 pitch (degrees)
 * @param {Function} [onRotationChange] - Optional (yaw, pitch) when user drags 360 view
 * @param {number} [controlledFov] - Optional master zoom/FOV (degrees, 20-120) when sync mode
 * @param {Function} [onFovChange] - Optional (fov) when user zooms with wheel in sync mode
 * @param {string} [controlledViewMode] - Optional master view mode ("fish"|"normal"|"quad"|"flat") when sync mode
 * @param {Function} [onViewModeChange] - Optional (mode) when user changes view mode in sync mode
 * @param {boolean} [controlledAutoRotate] - Optional master autorotation when sync mode
 * @param {Function} [onAutoRotateChange] - Optional (enabled) when user toggles autorotation in sync mode
 * @param {boolean} [hideVideoTimeline] - When true, hides the seek/timeline bar (e.g. Compare Patrol)
 */
export function PatrolVideoPlayer({
  videos = [],

  initialVideoIndex = 0,
  autoPlay = false,
  onSettingsClick,
  getCurrentVideoIndex,
  syncMode = false,
  hideVideoTimeline = false,
  syncValidForPlayback,
  controlledIsPlaying,
  controlledCurrentTime,
  controlledVideoIndex,
  onPlayPauseChange,
  onTimeUpdate,
  onVideoIndexChange,
  controlledYaw,
  controlledPitch,
  onRotationChange,
  controlledFov,
  onFovChange,
  controlledViewMode,
  onViewModeChange,
  controlledAutoRotate,
  onAutoRotateChange,
  leftPanelWidth = 35,
  selectedPatrolSequenceId,
  selectedPatrol = [],
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  // When sync mode and controlled value provided, use it; otherwise use local state
  const effectiveIsPlaying = isPlaying;
  const [viewMode, setViewMode] = useState("normal"); // "fish", "normal", "quad", "flat"
  const effectiveViewMode =
    syncMode && controlledViewMode !== undefined
      ? controlledViewMode
      : viewMode;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [volume, setVolume] = useState(1.0); // Volume range: 0-1
  const [isMuted, setIsMuted] = useState(true);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [selectedQuadView, setSelectedQuadView] = useState("front"); // "front", "rear", "left", "right"
  const [autoRotate, setAutoRotate] = useState(false); // Auto-rotate from 0 to 360 degrees
  const effectiveAutoRotate =
    syncMode && controlledAutoRotate !== undefined
      ? controlledAutoRotate
      : autoRotate;

  // Keep local state in sync when controlled props change (e.g. from other panel in Compare Patrol)
  useEffect(() => {
    if (syncMode && controlledViewMode !== undefined) {
      setViewMode(controlledViewMode);
    }
  }, [syncMode, controlledViewMode]);
  useEffect(() => {
    if (syncMode && controlledAutoRotate !== undefined) {
      setAutoRotate(controlledAutoRotate);
    }
  }, [syncMode, controlledAutoRotate]);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [controlsContainerWidth, setControlsContainerWidth] = useState(0);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const controlsContainerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const sphereRef = useRef(null);
  const videoTextureRef = useRef(null);
  const animationFrameRef = useRef(null);
  const resizeHandlerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  /** rAF id for container ResizeObserver (splitter drag). */
  const containerResizeRafRef = useRef(0);
  const lastWebGLLayoutSizeRef = useRef({ w: -1, h: -1 });
  const quadLastWebGLLayoutSizeRef = useRef({});
  const quadResizeHandlersRef = useRef({});

  // Quad view refs
  const quadCanvasRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadRendererRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadSceneRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadCameraRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadSphereRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadAnimationFrameRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });

  // Rotation state for different views
  const [rotation, setRotation] = useState({ yaw: 0, pitch: 0, fov: 75 });
  const [quadRotations, setQuadRotations] = useState({
    front: { yaw: 0, pitch: 0 },
    rear: { yaw: 180, pitch: 0 },
    left: { yaw: -90, pitch: 0 },
    right: { yaw: 90, pitch: 0 },
    main: { yaw: 0, pitch: 0 },
  });
  const quadRotationsRef = useRef({
    front: { yaw: 0, pitch: 0 },
    rear: { yaw: 180, pitch: 0 },
    left: { yaw: -90, pitch: 0 },
    right: { yaw: 90, pitch: 0 },
    main: { yaw: 0, pitch: 0 },
  });
  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef(null);
  const isDraggingQuadRef = useRef(false);
  const lastMousePosQuadRef = useRef(null);
  const isManualControlRef = useRef(false); // Track if user is manually controlling rotation
  const currentSmoothYawRef = useRef(0); // Current smooth yaw value for interpolation
  const targetYawRef = useRef(0); // Target yaw value to interpolate towards
  const autoRotateYawOffsetRef = useRef(0); // Offset so autorotation continues from last mouse angle
  const currentSmoothFovRef = useRef(75); // Current smooth FOV value for interpolation
  const targetFovRef = useRef(75); // Target FOV value to interpolate towards
  const rotationRef = useRef({ yaw: 0, pitch: 0, fov: 75 }); // Ref to track rotation without triggering re-renders
  const isResettingRef = useRef(false); // Track if we're resetting due to videos change
  const previousVideosLengthRef = useRef(videos?.length || 0); // Track previous videos length
  const prevSyncModeRef = useRef(syncMode); // Track previous sync mode for transition (sync off → pause)
  const syncPlayStateRef = useRef(false); // In sync mode, master play state so loadVideo preserves play/pause when changing video

  const [totalVideoDurations, setTotalVideoDurations] = useState(0);
  const [videoDurations, setVideoDurations] = useState([]); // Per-video durations for cumulative time
  const [currentTime, setCurrentTime] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(initialVideoIndex);
  // When sync mode and controlled value provided, use it for prev/next and UI so both panels stay in sync
  const effectiveVideoIndex =
    syncMode && controlledVideoIndex !== undefined
      ? controlledVideoIndex
      : currentVideoIndex;
  // const [progressPercent, setProgressPercent] = useState(0);

  useEffect(() => {
    if (!videos || videos.length === 0) return;
    const loadDuration = (videoUrl) =>
      new Promise((resolve) => {
        const video = document.createElement("video");
        video.src = videoUrl;
        video.preload = "metadata";

        video.onloadedmetadata = () => {
          resolve(video.duration || 0);
        };

        video.onerror = () => resolve(0);
      });

    Promise.all(videos.map(loadDuration)).then((durations) => {
      const total = durations.reduce((a, b) => a + b, 0);
      setTotalVideoDurations(total);
      setVideoDurations(durations);
    });
  }, [videos]);

  // Update currentTime as cumulative time across playlist (prev videos' duration + current video time)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const updateTime = () => {
      const timeBeforeCurrent = videoDurations
        .slice(0, currentVideoIndex)
        .reduce((a, b) => a + b, 0);
      const cumulativeTime = timeBeforeCurrent + video.currentTime;
      setCurrentTime(cumulativeTime);
    };

    updateTime(); // Set immediately when video/index changes
    video.addEventListener("timeupdate", updateTime);
    video.addEventListener("seeked", updateTime);
    return () => {
      video.removeEventListener("timeupdate", updateTime);
      video.removeEventListener("seeked", updateTime);
    };
  }, [currentVideoIndex, videoDurations]);

  /**
   * Initialize Three.js scene for 360-degree video rendering
   */
  const init360Renderer = useCallback(
    (canvas, video, initialFov = 75) => {
      if (!canvas || !video) return null;

      // Create renderer with improved settings for better quality
      const renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        antialias: true,
        alpha: false,
        powerPreference: "high-performance",
        precision: "highp",
      });

      // Allow higher pixel ratio for better quality on high-DPI displays
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 3));

      // Get dimensions from parent container to ensure 100% sizing
      const parent = canvas.parentElement;
      const width = parent
        ? parent.clientWidth
        : canvas.clientWidth || canvas.offsetWidth;
      const height = parent
        ? parent.clientHeight
        : canvas.clientHeight || canvas.offsetHeight;

      renderer.setSize(width, height);
      renderer.setClearColor(0x000000, 1.0);

      if (renderer.outputColorSpace !== undefined) {
        renderer.outputColorSpace = THREE.SRGBColorSpace;
      }

      // Create scene
      const scene = new THREE.Scene();

      // Create camera (using dimensions already calculated above)
      const camera = new THREE.PerspectiveCamera(
        initialFov,
        width / height,
        0.01,
        200,
      );
      camera.position.set(0, 0, 0);

      const videoTexture = createPatrolEquirectVideoTexture(video);
      const sphere = createPatrolEquirectSphereMesh(videoTexture);
      scene.add(sphere);

      return { renderer, scene, camera, sphere, videoTexture };
    },
    [videos],
  );

  /**
   * Initialize main 360 renderer
   */
  const initMain360Renderer = useCallback(() => {
    if (!canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;

    // Cleanup existing renderer
    if (rendererRef.current) {
      rendererRef.current.dispose();
    }

    const result = init360Renderer(canvas, video, rotationRef.current.fov);
    if (!result) return;

    rendererRef.current = result.renderer;
    sceneRef.current = result.scene;
    cameraRef.current = result.camera;
    sphereRef.current = result.sphere;
    videoTextureRef.current = result.videoTexture;
    {
      const parent0 = canvas.parentElement;
      const w0 = parent0
        ? parent0.clientWidth
        : canvas.clientWidth || canvas.offsetWidth;
      const h0 = parent0
        ? parent0.clientHeight
        : canvas.clientHeight || canvas.offsetHeight;
      lastWebGLLayoutSizeRef.current = { w: w0, h: h0 };
    }

    // Setup resize handler
    const handleResize = () => {
      if (
        !canvas ||
        !rendererRef.current ||
        !cameraRef.current ||
        !sceneRef.current
      )
        return;
      // Get dimensions from parent container to ensure 100% sizing
      const parent = canvas.parentElement;
      const width = parent
        ? parent.clientWidth
        : canvas.clientWidth || canvas.offsetWidth;
      const height = parent
        ? parent.clientHeight
        : canvas.clientHeight || canvas.offsetHeight;
      if (width <= 0 || height <= 0) return;

      const last = lastWebGLLayoutSizeRef.current;
      if (last.w === width && last.h === height) return;
      lastWebGLLayoutSizeRef.current = { w: width, h: height };

      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      const scene = sceneRef.current;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);

      const videoTexture = videoTextureRef.current;
      if (videoTexture && video && video.readyState >= 2) {
        videoTexture.needsUpdate = true;
      }
      renderer.render(scene, camera);
    };

    resizeHandlerRef.current = handleResize;
    window.addEventListener("resize", handleResize);

    // Render loop
    const animate = async () => {
      if (!rendererRef.current || !sceneRef.current || !cameraRef.current)
        return;

      // if (currentVideoIndex > 0) {
      //   targetFovRef.current = 75;
      //   await setRotation((prev) => ({ ...prev, fov: 75 }));
      // }
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const videoTexture = videoTextureRef.current;
      const video = videoRef.current;

      // Auto-rotate based on video progress if enabled, video is playing, and user is not manually controlling
      if (
        effectiveAutoRotate &&
        video &&
        !video.paused &&
        video.duration > 0 &&
        !isManualControlRef.current
      ) {
        const progress = video.currentTime / video.duration;
        // Use offset so autorotation continues from last angle after user drags 360 view
        const targetYaw = progress * 360 + autoRotateYawOffsetRef.current;
        targetYawRef.current = targetYaw;

        // Smooth interpolation towards target yaw
        // Using lerp factor of 0.01 for smooth transition
        const lerpFactor = 0.01;
        currentSmoothYawRef.current = THREE.MathUtils.lerp(
          currentSmoothYawRef.current,
          targetYawRef.current,
          lerpFactor,
        );

        // Update rotation ref (not state) to avoid triggering re-renders
        rotationRef.current.yaw = currentSmoothYawRef.current;
      } else {
        // When not auto-rotating, use rotation from ref (which is synced with state via useEffect)
        // No need to sync here as the useEffect handles it
      }

      // Smooth interpolation for FOV
      const fovLerpFactor = 0.05;
      currentSmoothFovRef.current = THREE.MathUtils.lerp(
        currentSmoothFovRef.current,
        targetFovRef.current,
        fovLerpFactor,
      );

      // Update camera rotation based on yaw and pitch from ref
      camera.rotation.order = "YXZ";
      camera.rotation.y = THREE.MathUtils.degToRad(rotationRef.current.yaw);
      camera.rotation.x = THREE.MathUtils.degToRad(rotationRef.current.pitch);
      camera.fov = currentSmoothFovRef.current;
      camera.updateProjectionMatrix();

      // Update video texture
      if (videoTexture && video && video.readyState >= 2) {
        videoTexture.needsUpdate = true;
      }

      renderer.render(scene, camera);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
  }, [effectiveAutoRotate, init360Renderer, currentVideoIndex]);

  // Reset autorotation offset when video changes so new video starts from 0
  useEffect(() => {
    autoRotateYawOffsetRef.current = 0;
  }, [currentVideoIndex]);

  /**
   * Initialize quad view renderers
   */
  const initQuadViewRenderers = useCallback(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const views = ["front", "rear", "left", "right", "main"];

    views.forEach((view) => {
      const canvas = quadCanvasRefs.current[view];
      if (!canvas) return;

      // Cleanup existing renderer
      if (quadAnimationFrameRefs.current[view]) {
        cancelAnimationFrame(quadAnimationFrameRefs.current[view]);
      }
      if (quadRendererRefs.current[view]) {
        quadRendererRefs.current[view].dispose();
      }

      const result = init360Renderer(canvas, video, 75);
      if (!result) return;

      quadRendererRefs.current[view] = result.renderer;
      quadSceneRefs.current[view] = result.scene;
      quadCameraRefs.current[view] = result.camera;
      quadSphereRefs.current[view] = result.sphere;

      {
        const parentQ = canvas.parentElement;
        const wq = parentQ
          ? parentQ.clientWidth
          : canvas.clientWidth || canvas.offsetWidth;
        const hq = parentQ
          ? parentQ.clientHeight
          : canvas.clientHeight || canvas.offsetHeight;
        quadLastWebGLLayoutSizeRef.current[view] = { w: wq, h: hq };
      }

      // Setup resize handler for each view
      const handleResize = () => {
        if (
          !canvas ||
          !quadRendererRefs.current[view] ||
          !quadCameraRefs.current[view] ||
          !quadSceneRefs.current[view]
        )
          return;
        // Get dimensions from parent container to ensure 100% sizing
        const parent = canvas.parentElement;
        const width = parent
          ? parent.clientWidth
          : canvas.clientWidth || canvas.offsetWidth;
        const height = parent
          ? parent.clientHeight
          : canvas.clientHeight || canvas.offsetHeight;
        if (width <= 0 || height <= 0) return;

        const lastQ = quadLastWebGLLayoutSizeRef.current[view];
        if (lastQ && lastQ.w === width && lastQ.h === height) return;
        quadLastWebGLLayoutSizeRef.current[view] = { w: width, h: height };

        const renderer = quadRendererRefs.current[view];
        const camera = quadCameraRefs.current[view];
        const scene = quadSceneRefs.current[view];
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);

        const videoTexture = result.videoTexture;
        if (videoTexture && video.readyState >= 2) {
          videoTexture.needsUpdate = true;
        }
        renderer.render(scene, camera);
      };

      quadResizeHandlersRef.current[view] = handleResize;
      window.addEventListener("resize", handleResize);

      // Render loop for each view
      const animate = () => {
        if (
          !quadRendererRefs.current[view] ||
          !quadSceneRefs.current[view] ||
          !quadCameraRefs.current[view]
        )
          return;

        const camera = quadCameraRefs.current[view];
        const renderer = quadRendererRefs.current[view];
        const scene = quadSceneRefs.current[view];
        const videoTexture = result.videoTexture;
        const rot = quadRotationsRef.current[view] || { yaw: 0, pitch: 0 };

        // Set camera rotation based on view
        camera.rotation.order = "YXZ";
        camera.rotation.y = THREE.MathUtils.degToRad(rot.yaw);
        camera.rotation.x = THREE.MathUtils.degToRad(rot.pitch);
        camera.updateProjectionMatrix();

        // Update video texture
        if (videoTexture && video.readyState >= 2) {
          videoTexture.needsUpdate = true;
        }

        renderer.render(scene, camera);
        quadAnimationFrameRefs.current[view] = requestAnimationFrame(animate);
      };

      animate();
    });
  }, [init360Renderer]);

  /**
   * Handle mouse down for 360 rotation.
   * When user interacts with screen, turn off rotation mode (auto-rotate) so manual control takes over.
   */
  const handleMouseDown = useCallback(
    (e) => {
      if (effectiveViewMode === "flat") return;
      // User interacting with screen: turn off auto-rotate (rotation mode)
      if (effectiveAutoRotate) {
        setAutoRotate(false);
        if (onAutoRotateChange) onAutoRotateChange(false);
      }
      // In quad view, only allow dragging on the main view (not on mini views)
      if (effectiveViewMode === "quad") {
        const target = e.target;
        // Check if click is on a mini view canvas (left side)
        if (target.closest && target.closest("[data-quad-mini-view]")) {
          return; // Don't allow dragging on mini views
        }
        isDraggingQuadRef.current = true;
        lastMousePosQuadRef.current = { x: e.clientX, y: e.clientY };
        isManualControlRef.current = true; // User is manually controlling
        // Sync smooth yaw with current rotation when manual control starts
        currentSmoothYawRef.current = rotation.yaw;
        targetYawRef.current = rotation.yaw;
      } else {
        isDraggingRef.current = true;
        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
        isManualControlRef.current = true; // User is manually controlling
        // Sync smooth yaw with current rotation when manual control starts
        currentSmoothYawRef.current = rotation.yaw;
        targetYawRef.current = rotation.yaw;
      }
    },
    [effectiveViewMode, effectiveAutoRotate, rotation.yaw, onAutoRotateChange],
  );

  /**
   * Handle mouse move for 360 rotation
   */
  const handleMouseMove = useCallback(
    (e) => {
      if (effectiveViewMode === "flat") return;

      if (effectiveViewMode === "quad") {
        if (!isDraggingQuadRef.current || !lastMousePosQuadRef.current) return;

        const dx = e.clientX - lastMousePosQuadRef.current.x;
        const dy = e.clientY - lastMousePosQuadRef.current.y;

        const SENSITIVITY = 0.3;

        setQuadRotations((prev) => {
          const currentRot = prev[selectedQuadView] || prev.main;
          const newYaw = currentRot.yaw + dx * SENSITIVITY;
          const newPitch = Math.max(
            -89,
            Math.min(89, currentRot.pitch + dy * SENSITIVITY),
          );
          const newRotations = {
            ...prev,
            [selectedQuadView]: { yaw: newYaw, pitch: newPitch },
            main: { yaw: newYaw, pitch: newPitch },
          };
          quadRotationsRef.current = newRotations;
          return newRotations;
        });
        if (onRotationChange) {
          const currentRot =
            quadRotationsRef.current[selectedQuadView] ||
            quadRotationsRef.current.main;
          const newYaw = currentRot.yaw + dx * SENSITIVITY;
          const newPitch = Math.max(
            -89,
            Math.min(89, currentRot.pitch + dy * SENSITIVITY),
          );
          onRotationChange(newYaw, newPitch);
        }

        lastMousePosQuadRef.current = { x: e.clientX, y: e.clientY };
      } else {
        if (!isDraggingRef.current || !lastMousePosRef.current) return;

        const dx = e.clientX - lastMousePosRef.current.x;
        const dy = e.clientY - lastMousePosRef.current.y;

        const SENSITIVITY = 0.3;

        const newYaw = rotationRef.current.yaw + dx * SENSITIVITY;
        const newPitch = Math.max(
          -89,
          Math.min(89, rotationRef.current.pitch + dy * SENSITIVITY),
        );

        setRotation((prev) => {
          rotationRef.current.yaw = newYaw;
          rotationRef.current.pitch = newPitch;
          return {
            ...prev,
            yaw: newYaw,
            pitch: newPitch,
          };
        });
        if (onRotationChange) {
          onRotationChange(newYaw, newPitch);
        }

        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      }
    },
    [effectiveViewMode, selectedQuadView, onRotationChange],
  );

  /**
   * Handle mouse up for 360 rotation
   */
  const handleMouseUp = useCallback(() => {
    // Capture current yaw before clearing drag state so autorotation can continue from this angle
    const currentYaw =
      isDraggingQuadRef.current && quadRotationsRef.current?.main != null
        ? quadRotationsRef.current.main.yaw
        : rotationRef.current.yaw;
    isDraggingRef.current = false;
    lastMousePosRef.current = null;
    isDraggingQuadRef.current = false;
    lastMousePosQuadRef.current = null;
    // Resume auto-rotation after a short delay; continue from last mouse angle instead of snapping to video progress
    setTimeout(() => {
      isManualControlRef.current = false;
      const video = videoRef.current;
      if (video && video.duration > 0 && effectiveAutoRotate) {
        const progress = video.currentTime / video.duration;
        // Store offset so autorotation continues from where user left the view
        autoRotateYawOffsetRef.current = currentYaw - progress * 360;
        currentSmoothYawRef.current = currentYaw;
        targetYawRef.current = currentYaw;
        rotationRef.current.yaw = currentYaw;
        setRotation((prev) => ({
          ...prev,
          yaw: currentYaw,
        }));
      }
    }, 100);
  }, [effectiveAutoRotate]);

  /**
   * Handle wheel for zoom in 360 mode
   */
  const handleWheel = useCallback(
    (e) => {
      if (effectiveViewMode === "flat") return;
      e.preventDefault();
      // User interacting with screen: turn off auto-rotate (rotation mode)
      if (effectiveAutoRotate) {
        setAutoRotate(false);
        if (onAutoRotateChange) onAutoRotateChange(false);
      }
      if (effectiveViewMode === "quad") {
        return;
      }

      const WHEEL_PAN_STEP = 0.12;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const newYaw = rotationRef.current.yaw + e.deltaX * WHEEL_PAN_STEP;
        const newPitch = rotationRef.current.pitch;
        rotationRef.current.yaw = newYaw;
        currentSmoothYawRef.current = newYaw;
        targetYawRef.current = newYaw;
        setRotation((prev) => ({ ...prev, yaw: newYaw }));
        if (onRotationChange) {
          onRotationChange(newYaw, newPitch);
        }
        return;
      }

      const zoomDelta = e.deltaY > 0 ? 3 : -3;
      setRotation((prev) => {
        const newFov = Math.max(20, Math.min(120, prev.fov + zoomDelta));
        targetFovRef.current = newFov;
        currentSmoothFovRef.current = newFov;
        rotationRef.current.fov = newFov;
        if (onFovChange) {
          onFovChange(newFov);
        }
        return {
          ...prev,
          fov: newFov,
        };
      });
    },
    [
      effectiveViewMode,
      effectiveAutoRotate,
      onFovChange,
      onAutoRotateChange,
      onRotationChange,
    ],
  );

  /**
   * Load and play video
   */
  const loadVideo = useCallback(
    async (index) => {
      // if (index > 0) {
      //   targetFovRef.current = 75;
      //   await setRotation((prev) => ({ ...prev, fov: 75 }));
      // }
      if (
        !videos ||
        videos.length === 0 ||
        index < 0 ||
        index >= videos.length
      ) {
        setError("No videos available");
        return;
      }

      setIsLoading(true);
      setError(null);

      const video = videoRef.current;
      if (!video) return;

      try {
        video.src = videos[index];
        video.load();

        await new Promise((resolve, reject) => {
          const handleLoadedData = () => {
            video.removeEventListener("loadeddata", handleLoadedData);
            video.removeEventListener("error", handleError);
            resolve();
          };

          const handleError = (e) => {
            video.removeEventListener("loadeddata", handleLoadedData);
            video.removeEventListener("error", handleError);
            reject(e);
          };

          video.addEventListener("loadeddata", handleLoadedData);
          video.addEventListener("error", handleError);
        });

        setIsLoading(false);
        // // Reset rotation to 0 when loading a new video
        // currentSmoothYawRef.current = 0;
        // targetYawRef.current = 0;
        // rotationRef.current = { yaw: 0, pitch: 0, fov: rotation.fov };
        // setRotation((prev) => ({ ...prev, yaw: 0, pitch: 0 }));
        // Reset manual control flag
        isManualControlRef.current = false;
        // Sync mode: preserve current play/pause state when changing video. Non-sync: index 0 paused, index !== 0 auto-play.
        if (syncMode) {
          const shouldPlay = syncPlayStateRef.current;
          if (shouldPlay) {
            await video.play().catch(() => {});
            setIsPlaying(true);
            if (onPlayPauseChange) onPlayPauseChange(true);
          } else {
            video.pause();
            setIsPlaying(false);
            if (onPlayPauseChange) onPlayPauseChange(false);
          }
        } else {
          if (index !== 0) {
            await video.play().catch(() => {});
            setIsPlaying(true);
          } else {
            setIsPlaying(false);
          }
        }
      } catch (err) {
        console.error("Error loading video:", err);
        setError("Failed to load video");
        setIsLoading(false);
      }
    },
    [videos, syncMode, onPlayPauseChange],
  );

  /**
   * Handle play/pause toggle.
   * Uses video.paused (actual DOM state) in non-sync mode so rapid clicks and external pauses stay correct.
   */
  const handlePlayPause = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (video.paused) {
        await video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    } catch (err) {
      console.error("Error playing/pausing video:", err);
    }
  }, [syncMode, onPlayPauseChange, effectiveIsPlaying]);

  /**
   * Handle previous video (uses effectiveVideoIndex in sync mode so both panels stay in sync).
   * In sync mode, preserve play/pause state: if playing, next video plays; if paused, stays paused.
   */
  const handlePrevious = useCallback(() => {
    if (effectiveVideoIndex > 0) {
      const newIndex = effectiveVideoIndex - 1;
      setCurrentVideoIndex(newIndex);
      if (!syncMode) setIsPlaying(false);
      if (syncMode && onVideoIndexChange) onVideoIndexChange(newIndex);
    }
  }, [effectiveVideoIndex, videos, syncMode, onVideoIndexChange]);

  /**
   * Handle next video (uses effectiveVideoIndex in sync mode so both panels stay in sync).
   * In sync mode, preserve play/pause state: if playing, next video plays; if paused, stays paused.
   */
  const handleNext = useCallback(() => {
    if (effectiveVideoIndex < videos.length - 1) {
      const newIndex = effectiveVideoIndex + 1;
      setCurrentVideoIndex(newIndex);
      if (!syncMode) setIsPlaying(false);
      if (syncMode && onVideoIndexChange) onVideoIndexChange(newIndex);
    }
  }, [effectiveVideoIndex, videos, syncMode, onVideoIndexChange]);

  /**
   * Set view mode
   */
  const setViewModeHandler = useCallback(
    (mode) => {
      if (onViewModeChange) {
        onViewModeChange(mode);
      }
      setViewMode(mode);
      // Set FOV based on mode
      if (mode === "fish") {
        targetFovRef.current = 180;
        rotationRef.current.fov = 110;
        setRotation((prev) => ({ ...prev, fov: 110 })); // Wide FOV for fisheye effect
      } else if (mode === "normal") {
        targetFovRef.current = 75;
        rotationRef.current.fov = 75;
        setRotation((prev) => ({ ...prev, fov: 75 })); // Moderate FOV
      } else if (mode === "quad") {
        targetFovRef.current = 75;
        rotationRef.current.fov = 75;
        setRotation((prev) => ({ ...prev, fov: 75 }));
      }
    },
    [onViewModeChange],
  );

  /**
   * Handle quad view selection
   */
  const handleQuadViewSelect = useCallback((view) => {
    setSelectedQuadView(view);
    // Update main rotation to match selected view
    setQuadRotations((prev) => {
      const newRotations = {
        ...prev,
        main: { ...prev[view] },
      };
      quadRotationsRef.current = newRotations;
      return newRotations;
    });
  }, []);

  // Initialize and sync quad rotations ref with state
  useEffect(() => {
    quadRotationsRef.current = {
      front: { yaw: 0, pitch: 0 },
      rear: { yaw: 180, pitch: 0 },
      left: { yaw: -90, pitch: 0 },
      right: { yaw: 90, pitch: 0 },
      main: { yaw: 0, pitch: 0 },
    };
  }, []);

  useEffect(() => {
    quadRotationsRef.current = quadRotations;
  }, [quadRotations]);

  // Sync rotationRef with rotation state (for animation loop)
  useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);

  /**
   * Handle volume change
   */
  const handleVolumeChange = useCallback((event, newValue) => {
    const newVolume = Array.isArray(newValue) ? newValue[0] : newValue;
    const video = videoRef.current;
    if (video) {
      video.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  }, []);

  /**
   * Toggle mute/unmute
   */
  const handleToggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      // Unmute: restore previous volume or default to 0.7
      const newVolume = volume > 0 ? volume : 0.7;
      video.muted = false;
      video.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(false);
    } else {
      // Mute: save current volume and set to 0
      video.muted = true;
      video.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  /**
   * Toggle fullscreen mode
   */
  const handleToggleFullscreen = useCallback(() => {
    const container = playerContainerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, []);

  /**
   * Toggle auto-rotation for patrol
   */
  const handleToggleAutoRotate = useCallback(() => {
    const newAutoRotate = !effectiveAutoRotate;
    if (onAutoRotateChange) {
      onAutoRotateChange(newAutoRotate);
    }
    setAutoRotate(newAutoRotate);

    if (newAutoRotate) {
      // When resuming auto-rotation, continue from current view angle (don't snap to video progress)
      isManualControlRef.current = false;
      const video = videoRef.current;
      if (video && video.duration > 0) {
        const progress = video.currentTime / video.duration;
        const currentYaw = rotation.yaw;
        autoRotateYawOffsetRef.current = currentYaw - progress * 360;
        currentSmoothYawRef.current = currentYaw;
        targetYawRef.current = currentYaw;
        rotationRef.current.yaw = currentYaw;
        setRotation((prev) => ({
          ...prev,
          yaw: currentYaw,
        }));
      }
    } else {
      // When pausing auto-rotation, keep current rotation position
      isManualControlRef.current = true;
      currentSmoothYawRef.current = rotation.yaw;
      targetYawRef.current = rotation.yaw;
      rotationRef.current.yaw = rotation.yaw;
    }
  }, [effectiveAutoRotate, onAutoRotateChange, rotation.yaw]);

  /**
   * Handle menu open
   */
  const handleMenuOpen = useCallback((event) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  /**
   * Handle menu close
   */
  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  /**
   * Handle view mode change from menu
   */
  const handleMenuViewModeChange = useCallback(
    (mode) => {
      setViewModeHandler(mode);
      handleMenuClose();
    },
    [setViewModeHandler, handleMenuClose],
  );

  /**
   * Handle fullscreen from menu
   */
  const handleMenuFullscreen = useCallback(() => {
    handleToggleFullscreen();
    handleMenuClose();
  }, [handleToggleFullscreen, handleMenuClose]);

  /**
   * Handle play/pause from menu
   */
  const handleMenuPlayPause = useCallback(() => {
    handlePlayPause();
    handleMenuClose();
  }, [handlePlayPause, handleMenuClose]);

  // Initialize renderer when mode changes or video loads
  useEffect(() => {
    if (effectiveViewMode === "fish" || effectiveViewMode === "normal") {
      // Wait for canvas to be available (it might be unmounted when switching from quad/flat)
      const timer = setTimeout(() => {
        if (videoRef.current && canvasRef.current) {
          initMain360Renderer();
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        // Cleanup renderer
        if (resizeHandlerRef.current) {
          window.removeEventListener("resize", resizeHandlerRef.current);
          resizeHandlerRef.current = null;
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        if (rendererRef.current) {
          rendererRef.current.dispose();
          rendererRef.current = null;
        }
        if (videoTextureRef.current) {
          videoTextureRef.current.dispose();
          videoTextureRef.current = null;
        }
        if (sceneRef.current) {
          sceneRef.current = null;
        }
        if (cameraRef.current) {
          cameraRef.current = null;
        }
        if (sphereRef.current) {
          sphereRef.current = null;
        }
      };
    } else if (effectiveViewMode === "quad") {
      // Wait for canvases to be available
      const timer = setTimeout(() => {
        if (videoRef.current) {
          initQuadViewRenderers();
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        quadResizeHandlersRef.current = {};
        // Cleanup quad renderers
        Object.keys(quadAnimationFrameRefs.current).forEach((view) => {
          if (quadAnimationFrameRefs.current[view]) {
            cancelAnimationFrame(quadAnimationFrameRefs.current[view]);
            quadAnimationFrameRefs.current[view] = null;
          }
        });
        Object.keys(quadRendererRefs.current).forEach((view) => {
          if (quadRendererRefs.current[view]) {
            quadRendererRefs.current[view].dispose();
            quadRendererRefs.current[view] = null;
          }
        });
      };
    } else {
      // Cleanup renderer when switching to flat mode
      if (resizeHandlerRef.current) {
        window.removeEventListener("resize", resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
        rendererRef.current = null;
      }
      if (videoTextureRef.current) {
        videoTextureRef.current.dispose();
        videoTextureRef.current = null;
      }
    }
  }, [effectiveViewMode, initMain360Renderer, initQuadViewRenderers]);

  // ResizeObserver: when the player container resizes (e.g. Patrol List toggled, splitter dragged),
  // update canvas/renderer size so the video fills the full width instead of showing a black bar.
  useEffect(() => {
    const container = containerRef.current;
    if (
      !container ||
      (effectiveViewMode !== "fish" &&
        effectiveViewMode !== "normal" &&
        effectiveViewMode !== "quad")
    ) {
      return;
    }

    const runResizeHandlers = () => {
      if (resizeHandlerRef.current) {
        resizeHandlerRef.current();
      }
      Object.values(quadResizeHandlersRef.current).forEach((fn) => {
        if (typeof fn === "function") fn();
      });
    };

    const scheduleContainerResize = () => {
      if (containerResizeRafRef.current) return;
      containerResizeRafRef.current = requestAnimationFrame(() => {
        containerResizeRafRef.current = 0;
        runResizeHandlers();
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      scheduleContainerResize();
    });
    resizeObserver.observe(container);
    resizeObserverRef.current = resizeObserver;

    return () => {
      resizeObserver.disconnect();
      resizeObserverRef.current = null;
      if (containerResizeRafRef.current) {
        cancelAnimationFrame(containerResizeRafRef.current);
        containerResizeRafRef.current = 0;
      }
    };
  }, [effectiveViewMode]);

  // Load video when index changes or when videos array changes.
  // When sync mode is on, load only if sync validation passes (both panels have compatible patrols).
  useEffect(() => {
    if (!videos || videos.length === 0 || currentVideoIndex >= videos.length)
      return;
    if (syncMode && syncValidForPlayback === false) return;
    loadVideo(currentVideoIndex);
  }, [currentVideoIndex, videos, loadVideo, syncMode, syncValidForPlayback]);

  // Sync volume with video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = isMuted;
    video.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  // Sync rotationRef with rotation state (but only when not in animation loop)
  useEffect(() => {
    // Only update ref if it's significantly different to avoid unnecessary updates
    if (
      Math.abs(rotationRef.current.yaw - rotation.yaw) > 0.1 ||
      Math.abs(rotationRef.current.pitch - rotation.pitch) > 0.1
    ) {
      rotationRef.current.yaw = rotation.yaw;
      rotationRef.current.pitch = rotation.pitch;
    }
    rotationRef.current.fov = rotation.fov;
  }, [rotation]);

  // Sync target FOV ref with rotation.fov when it changes
  useEffect(() => {
    // Only sync if they differ significantly to avoid overriding explicit updates
    if (Math.abs(targetFovRef.current - rotation.fov) > 0.5) {
      targetFovRef.current = rotation.fov;
      // Also update current smooth FOV if it's far from the target
      if (Math.abs(currentSmoothFovRef.current - rotation.fov) > 5) {
        currentSmoothFovRef.current = rotation.fov;
      }
    }
  }, [rotation.fov]);

  // Setup mouse event listeners for 360 modes
  useEffect(() => {
    if (effectiveViewMode === "flat") return;

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    if (effectiveViewMode !== "quad") {
      container.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("wheel", handleWheel);
    };
  }, [
    effectiveViewMode,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
  ]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      // Sync mode: keep parent state in sync so Play/Pause button shows correct icon
      if (syncMode && onPlayPauseChange) {
        onPlayPauseChange(true);
      }
    };
    const handlePause = () => {
      setIsPlaying(false);
      // Sync mode: keep parent state in sync so Play/Pause button shows correct icon
      if (syncMode && onPlayPauseChange) {
        onPlayPauseChange(false);
      }
    };
    const handleEnded = async () => {
      const nextIndex = currentVideoIndex + 1;
      const hasNext = currentVideoIndex < videos.length - 1;

      // Only pause when last video ends; otherwise next video will auto-play (play one by one, pause only on Pause button)
      if (!hasNext) {
        setIsPlaying(false);
        if (syncMode && onPlayPauseChange) {
          onPlayPauseChange(false);
        }
      }

      // Auto-play next video if available (keep playing state so loadVideo plays the next)
      if (hasNext) {
        syncPlayStateRef.current = true; // So next video auto-plays even if pause event fired after ended
        setTimeout(() => {
          setCurrentVideoIndex((prev) => prev + 1);
          if (syncMode && onVideoIndexChange) {
            onVideoIndexChange(nextIndex);
          }
        }, 300);
      }
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [
    currentVideoIndex,
    videos.length,
    syncMode,
    onTimeUpdate,
    onVideoIndexChange,
    onPlayPauseChange,
  ]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
    };
  }, []);

  // Reset currentVideoIndex when videos array changes
  useEffect(() => {
    const currentLength = videos?.length || 0;
    if (currentLength !== previousVideosLengthRef.current) {
      isResettingRef.current = true;
      previousVideosLengthRef.current = currentLength;
      setCurrentVideoIndex(0);
      // Use requestAnimationFrame to reset flag after state update
      requestAnimationFrame(() => {
        isResettingRef.current = false;
      });
    }
  }, [videos]);

  // Clamp currentVideoIndex when it is out of bounds (e.g. sync sent index 5 but only 5 videos)
  useEffect(() => {
    if (!videos || videos.length === 0) return;
    const maxIndex = Math.max(0, videos.length - 1);
    if (currentVideoIndex > maxIndex) {
      setCurrentVideoIndex(maxIndex);
    }
  }, [videos, currentVideoIndex]);

  // Notify parent component of currentVideoIndex changes (but not during reset)
  useEffect(() => {
    if (getCurrentVideoIndex && !isResettingRef.current) {
      getCurrentVideoIndex(currentVideoIndex);
    }
  }, [currentVideoIndex, getCurrentVideoIndex]);

  // Sync mode: apply controlled play/pause to video element
  useEffect(() => {
    if (!syncMode || controlledIsPlaying === undefined) return;
    const video = videoRef.current;
    if (!video) return;
    if (controlledIsPlaying) {
      video.play().catch((err) => {
        console.error("Error applying controlled play state:", err);
        onPlayPauseChange?.(false);
      });
    } else {
      video.pause();
    }
  }, [syncMode, controlledIsPlaying, onPlayPauseChange]);

  // Keep sync play state ref updated so loadVideo can preserve play/pause when changing video (Prev/Next)
  useEffect(() => {
    if (syncMode && controlledIsPlaying !== undefined) {
      syncPlayStateRef.current = controlledIsPlaying;
    }
  }, [syncMode, controlledIsPlaying]);

  // When sync mode turns OFF (unlock), pause video so playback is off in both panels
  useEffect(() => {
    if (prevSyncModeRef.current === true && syncMode === false) {
      const video = videoRef.current;
      if (video) {
        video.pause();
        setIsPlaying(false);
      }
    }
    prevSyncModeRef.current = syncMode;
  }, [syncMode]);

  // Sync mode: apply controlled video index so both panels show same video (prev/next sync).
  // When sync validation fails (e.g. patrols incompatible), do not load video.
  useEffect(() => {
    if (!syncMode || controlledVideoIndex === undefined) return;
    if (syncValidForPlayback === false) return;
    if (
      controlledVideoIndex < 0 ||
      controlledVideoIndex >= (videos?.length || 0)
    )
      return;
    if (controlledVideoIndex === currentVideoIndex) return;
    isResettingRef.current = true;
    setCurrentVideoIndex(controlledVideoIndex);
    loadVideo(controlledVideoIndex);
    requestAnimationFrame(() => {
      isResettingRef.current = false;
    });
  }, [
    syncMode,
    controlledVideoIndex,
    videos?.length,
    currentVideoIndex,
    loadVideo,
  ]);

  // Sync mode: apply controlled 360 rotation (yaw, pitch) so other panel follows
  useEffect(() => {
    if (!syncMode) return;
    const yaw = controlledYaw;
    const pitch = controlledPitch;
    if (yaw === undefined && pitch === undefined) return;
    const newYaw =
      typeof yaw === "number" && isFinite(yaw) ? yaw : rotationRef.current.yaw;
    const newPitch =
      typeof pitch === "number" && isFinite(pitch)
        ? Math.max(-89, Math.min(89, pitch))
        : rotationRef.current.pitch;
    rotationRef.current.yaw = newYaw;
    rotationRef.current.pitch = newPitch;
    currentSmoothYawRef.current = newYaw;
    targetYawRef.current = newYaw;
    setRotation((prev) => ({ ...prev, yaw: newYaw, pitch: newPitch }));
    const rot = { yaw: newYaw, pitch: newPitch };
    quadRotationsRef.current = {
      front: rot,
      rear: rot,
      left: rot,
      right: rot,
      main: rot,
    };
    setQuadRotations({
      front: rot,
      rear: rot,
      left: rot,
      right: rot,
      main: rot,
    });
  }, [syncMode, controlledYaw, controlledPitch]);

  // Sync mode: apply controlled zoom (FOV) so other panel follows
  useEffect(() => {
    if (!syncMode || controlledFov === undefined) return;
    const fov =
      typeof controlledFov === "number" && isFinite(controlledFov)
        ? Math.max(20, Math.min(120, controlledFov))
        : rotationRef.current.fov;
    targetFovRef.current = fov;
    currentSmoothFovRef.current = fov;
    rotationRef.current.fov = fov;
    setRotation((prev) => ({ ...prev, fov }));
  }, [syncMode, controlledFov]);

  // Track controls container width for responsive menu
  useEffect(() => {
    const updateControlsWidth = () => {
      if (controlsContainerRef.current) {
        setControlsContainerWidth(controlsContainerRef.current.offsetWidth);
      }
    };

    updateControlsWidth();
    const resizeObserver = new ResizeObserver(updateControlsWidth);

    if (controlsContainerRef.current) {
      resizeObserver.observe(controlsContainerRef.current);
    }

    window.addEventListener("resize", updateControlsWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateControlsWidth);
    };
  }, []);

  if (!videos || videos.length === 0) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6B7280",
        }}
      >
        No videos available
      </Box>
    );
  }

  return (
    <Box
      ref={playerContainerRef}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#000",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
        // marginTop: 8,
      }}
    >
      {/* Video Container */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,

          cursor:
            effectiveViewMode !== "flat"
              ? isDraggingRef.current || isDraggingQuadRef.current
                ? "grabbing"
                : "grab"
              : "default",
        }}
      >
        {/* Volume Control - Top Right Corner (over video only) */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 16,
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            bgcolor: "rgba(0, 0, 0, 0.6)",
            borderRadius: 2,
            p: 1,
            maxWidth: "calc(100% - 32px)",
          }}
          onMouseEnter={() => setShowVolumeSlider(true)}
          onMouseLeave={() => setShowVolumeSlider(false)}
        >
          <IconButton
            onClick={handleToggleMute}
            sx={{
              color: "#fff",
            }}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>
          {showVolumeSlider && (
            <Slider
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              min={0}
              max={1}
              step={0.01}
              orientation="vertical"
              sx={{
                height: 100,
                color: "#fff",
                "& .MuiSlider-thumb": {
                  color: "#fff",
                },
                "& .MuiSlider-track": {
                  color: "#fff",
                },
                "& .MuiSlider-rail": {
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            />
          )}
        </Box>

        {/* Persistent video element - always rendered to maintain state */}
        <Box
          component="video"
          ref={videoRef}
          sx={{
            display: effectiveViewMode === "flat" ? "block" : "none",
            ...(effectiveViewMode === "flat" && {
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              transform: "translateZ(0)",
              backfaceVisibility: "hidden",
            }),
          }}
          onClick={effectiveViewMode === "flat" ? handlePlayPause : undefined}
          crossOrigin="anonymous"
        />

        {effectiveViewMode === "quad" ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              height: "100%",
              display: "flex",
              minWidth: 0,
            }}
          >
            {/* Left side - 4 mini views */}
            <Box
              data-quad-mini-view="true"
              sx={{
                width: "30%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                p: 1,
                bgcolor: "rgba(0, 0, 0, 0.5)",
              }}
            >
              {["front", "rear", "left", "right"].map((view) => (
                <Box
                  key={view}
                  data-quad-mini-view="true"
                  onClick={() => handleQuadViewSelect(view)}
                  sx={{
                    flex: 1,
                    position: "relative",
                    border:
                      selectedQuadView === view
                        ? "2px solid #4CAF50"
                        : "2px solid transparent",
                    borderRadius: 1,
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="canvas"
                    ref={(el) => (quadCanvasRefs.current[view] = el)}
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      height: "100%",
                      display: "block",
                      minWidth: 0,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 4,
                      left: 4,
                      color: "#fff",
                      fontSize: "10px",
                      textTransform: "capitalize",
                      bgcolor: "rgba(0, 0, 0, 0.6)",
                      px: 0.5,
                      borderRadius: 0.5,
                    }}
                  >
                    {view}
                  </Box>
                </Box>
              ))}
            </Box>
            {/* Right side - main rotatable view */}
            <Box
              sx={{
                flex: 1,
                position: "relative",
              }}
            >
              <Box
                component="canvas"
                ref={(el) => (quadCanvasRefs.current.main = el)}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  height: "100%",
                  display: "block",
                  minWidth: 0,
                }}
              />
            </Box>
          </Box>
        ) : effectiveViewMode === "flat" ? null : (
          <Box
            component="canvas"
            ref={canvasRef}
            sx={{
              width: "100%",
              maxWidth: "100%",
              height: "100%",
              display: "block",
              minWidth: 0,
            }}
          />
        )}

        {/* Loading indicator */}
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#fff",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Error message */}
        {error && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#ff4444",
              textAlign: "center",
              p: 2,
            }}
          >
            {error}
          </Box>
        )}
      </Box>
      {!hideVideoTimeline && (
        <Box sx={{ px: 2, pt: 1, pb: 0.5, bgcolor: "rgba(0, 0, 0, 0.8)" }}>
          {/* SEEK BAR */}
          <Slider
            min={0}
            max={totalVideoDurations}
            value={currentTime}
            step={0.1}
            onChange={(_, value) => setCurrentTime(value)}
            onChangeCommitted={(_, value) => {
              videoRef.current.currentTime = value;
            }}
            sx={{
              mt: 1,
              height: 5,
              color: "#2932e5",
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
              },
              "& .MuiSlider-rail": {
                opacity: 0.3,
                backgroundColor: "white",
              },
            }}
          />
        </Box>
      )}
      {/* Controls */}
      <Box
        ref={controlsContainerRef}
        sx={{
          bgcolor: "rgba(0, 0, 0, 0.8)",
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Left side - Playback controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            minWidth: 0,
            flexShrink: 1,
            flex: 1,
            overflow: "hidden",
            pr: 1,
          }}
        >
          {/* Previous Button */}
          <IconButton
            onClick={handlePrevious}
            disabled={effectiveVideoIndex === 0}
            sx={{
              color: "#fff",
              flexShrink: 0,
              "&:disabled": {
                color: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            <SkipPreviousIcon />
          </IconButton>

          {/* Auto-Rotation Patrol Control */}
          {effectiveViewMode !== "flat" && (
            <IconButton
              onClick={handleToggleAutoRotate}
              disabled={isLoading || !!error}
              sx={{
                color: effectiveAutoRotate ? "#4CAF50" : "#fff",
                position: "relative",
                flexShrink: 0,
                "&:disabled": {
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
              title={
                effectiveAutoRotate
                  ? "Pause Auto Rotation (Patrol)"
                  : "Start Auto Rotation (Patrol)"
              }
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SyncIcon sx={{ fontSize: "1.5em" }} />
                {effectiveAutoRotate ? (
                  <PauseIcon
                    sx={{
                      position: "absolute",
                      fontSize: "0.6em",
                      bgcolor: "rgba(0, 0, 0, 0.7)",
                      borderRadius: "50%",
                      p: 0.2,
                    }}
                  />
                ) : (
                  <PlayArrowIcon
                    sx={{
                      position: "absolute",
                      fontSize: "0.6em",
                      bgcolor: "rgba(0, 0, 0, 0.7)",
                      borderRadius: "50%",
                      p: 0.2,
                      ml: 0.1,
                    }}
                  />
                )}
              </Box>
            </IconButton>
          )}

          {/* Next Button */}
          <IconButton
            onClick={handleNext}
            disabled={effectiveVideoIndex === videos.length - 1}
            sx={{
              color: "#fff",
              flexShrink: 0,
              "&:disabled": {
                color: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            <SkipNextIcon />
          </IconButton>

          {/* Video Counter */}
          <Box
            sx={{
              color: "#fff",
              fontSize: "14px",
              ml: 2,
              px: 2,
            }}
          >
            {Math.min(effectiveVideoIndex + 1, Math.max(1, videos.length))} /{" "}
            {videos.length}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              ml: 2,
              borderLeft: "1px solid rgba(255, 255, 255, 0.2)",
              pl: 2,
            }}
          >
            {/* Play/Pause Button */}
            <IconButton
              onClick={handlePlayPause}
              disabled={isLoading || !!error}
              sx={{
                color: "#fff",
                "&:disabled": {
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              {effectiveIsPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Right side - View Mode Buttons */}
        {leftPanelWidth <= 50 && (
          <>
            {controlsContainerWidth > 0 && controlsContainerWidth < 600 ? (
              <>
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{
                    color: "#fff",
                    flexShrink: 0,
                    minWidth: 40,
                  }}
                  title="More options"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      bgcolor: "rgba(0, 0, 0, 0.9)",
                      color: "#fff",
                      minWidth: 200,
                    },
                  }}
                >
                  <MenuItem
                    onClick={handleMenuPlayPause}
                    disabled={isLoading || !!error}
                  >
                    <ListItemIcon>
                      {effectiveIsPlaying ? (
                        <PauseIcon sx={{ color: "#fff" }} />
                      ) : (
                        <PlayArrowIcon sx={{ color: "#fff" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText>
                      {effectiveIsPlaying ? "Pause" : "Play"}
                    </ListItemText>
                  </MenuItem>
                  {/* <MenuItem
                    onClick={() => handleMenuViewModeChange("fish")}
                    selected={effectiveViewMode === "fish"}
                  >
                    <ListItemIcon>
                      <LensIcon
                        sx={{ color: effectiveViewMode === "fish" ? "#4CAF50" : "#fff" }}
                      />
                    </ListItemIcon>
                    <ListItemText>Fish View</ListItemText>
                  </MenuItem> */}
                  <MenuItem
                    onClick={() => handleMenuViewModeChange("normal")}
                    selected={effectiveViewMode === "normal"}
                  >
                    <ListItemIcon>
                      <ThreeDRotationIcon
                        sx={{
                          color:
                            effectiveViewMode === "normal" ? "#4CAF50" : "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText>First Person View</ListItemText>
                  </MenuItem>
                  {/* <MenuItem
                    onClick={() => handleMenuViewModeChange("quad")}
                    selected={effectiveViewMode === "quad"}
                  >
                    <ListItemIcon>
                      <ViewSidebarIcon
                        sx={{
                          rotate: "180deg",
                          color:
                            effectiveViewMode === "quad" ? "#4CAF50" : "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText>Quad View</ListItemText>
                  </MenuItem> */}
                  <MenuItem
                    onClick={() => handleMenuViewModeChange("flat")}
                    selected={effectiveViewMode === "flat"}
                  >
                    <ListItemIcon>
                      <PanoramaHorizontalSelectIcon
                        sx={{
                          color:
                            effectiveViewMode === "flat" ? "#4CAF50" : "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText>Equirectangular View</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleMenuFullscreen}>
                    <ListItemIcon>
                      {isFullscreen ? (
                        <FullscreenExitIcon sx={{ color: "#fff" }} />
                      ) : (
                        <FullscreenIcon sx={{ color: "#fff" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText>
                      {isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              /* Show individual buttons when width is large enough */
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, sm: 1 },
                  flexShrink: 0,
                  minWidth: 0,
                }}
              >
                {/* <IconButton
                  onClick={() => setViewModeHandler("fish")}
                  sx={{
                    color: effectiveViewMode === "fish" ? "#4CAF50" : "#fff",
                  }}
                  title="Fish View"
                >
                  <LensIcon />
                </IconButton> */}
                <IconButton
                  onClick={() => setViewModeHandler("normal")}
                  sx={{
                    color: effectiveViewMode === "normal" ? "#4CAF50" : "#fff",
                  }}
                  title="First Person View"
                >
                  <ThreeDRotationIcon />
                </IconButton>
                {/* <IconButton
                  onClick={() => setViewModeHandler("quad")}
                  sx={{
                    color: effectiveViewMode === "quad" ? "#4CAF50" : "#fff",
                  }}
                  title="Quad View"
                >
                  <ViewSidebarIcon sx={{ rotate: "180deg" }} />
                </IconButton> */}
                <IconButton
                  onClick={() => setViewModeHandler("flat")}
                  sx={{
                    color: effectiveViewMode === "flat" ? "#4CAF50" : "#fff",
                  }}
                  title="Equirectangular View"
                >
                  <PanoramaHorizontalSelectIcon />
                </IconButton>
                <IconButton
                  onClick={handleToggleFullscreen}
                  sx={{
                    color: "#fff",
                  }}
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
